import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { selectInstructions, selectSubAccountId, selectSubmitDeleteStandingAuthState } from '../../../account-info-header/reducers';
import { TransactionType } from '../../types/Transaction';
import { ToastrService } from 'ngx-toastr';
import { deleteTransactionClicked, resetSubmitCancelTransactionState } from '../../../create-request/actions/standing-authorizations-table.actions';
import { AchActions, CheckActions, InternalTransferActions, StandingAuthorizationsTableActions, WireActions } from '../../../create-request/actions';
import { CashieringTransaction, RecurringTransaction, Sloa } from '../../../create-request/models';
import { StandingInstruction } from '../../types/StandingInstruction';
import { MediaTypeCode, StandingAuthDeleteMessaging, transactionDirection, transactionTypes } from '../../utilities/Constants';
import { Router } from '@angular/router';

@Component({
  standalone: false,
  selector: 'delete-standing-auth-modal',
  templateUrl: './delete-standing-auth-modal.component.html',
  styleUrls: ['./delete-standing-auth-modal.component.scss']
})
export class DeleteStandingAuthModalComponent implements OnInit, OnDestroy, AfterViewInit{
  @Input() showModal$: Observable<boolean>;
  @Input() currentTransaction$: Observable<string | null>;
  private pendingTransactionFound$ = new BehaviorSubject<boolean>(false);
  pendingTransactionFoundAsObservable$ = this.pendingTransactionFound$.asObservable();
  destroy$: Subject<boolean> = new Subject<boolean>();
  modal: EverestModal;
  modalId: string;
  title: string = StandingAuthDeleteMessaging.deleteTitle;
  message: string= StandingAuthDeleteMessaging.deleteMessage;
  continueButtonTxt: string = StandingAuthDeleteMessaging.deleteButtonText;
  showModalSub: Subscription;
  standingAuthId:string = "";
  subAccountId: string;
  instructionToDelete: StandingInstruction;
  currentTransaction: CashieringTransaction | RecurringTransaction | Sloa;
  instructions: StandingInstruction[];
  transactionType: string = "";
  nickname: string = "";
  toFrom: string = "";
  status: string = "";
  pendingerror: boolean = false;
  defaultInstruction: StandingInstruction = {
    isSloaSetup: false,
    processDate: '',
    fundsDirection: transactionDirection.out,
    isRecurring: false,
    txnCode: '',
    frequencyCode: ' ',
    to1: '',
    to2: '',
    to3: '',
    isViewable: false,
    isCancelable: false,
    isEditable: false,
    isReactivatable: false,
    cashieringRqstId: '',
    cashieringRqstSecondId: '',
    statusCd: '',
    statusCodeDescription: 'Pending',
    address: '',
    nickname: '',
    transactionType: 'internal-transfer',
    masterAccountId: '',
    isThirdParty: false,
    isBundled: false,
    isStandingInstruction: false,
    isRequestTrans: false,
    mediaTypeCd: '',
    typeDescription: '',
    bundledSloaIn: false,
    thirdPartyInd: false,
    faMasterAcct: ''
  }
  isAch: boolean;

  constructor(
    private store: Store, 
    private toastr: ToastrService, 
    private router: Router, 
  ) {}

  ngOnInit(): void {
    this.modalId = 'delete-link-modal-' + new Date().getTime().toString();
    this.store.select(selectSubAccountId).pipe(takeUntil(this.destroy$)).subscribe(subAccountId => {
      this.subAccountId = subAccountId
    });

    this.store.select(selectInstructions).pipe(takeUntil(this.destroy$)).subscribe(instructions => {
      this.instructions = instructions
    });

    this.currentTransaction$
    .pipe(takeUntil(this.destroy$))
    .subscribe((standingAuthId) => {
      if (standingAuthId){
        this.standingAuthId = standingAuthId
        };
        var splitRequestID = this.standingAuthId.split('-');
        if (splitRequestID != null)
        {
          this.standingAuthId = splitRequestID[1];
        }
        this.instructionToDelete = this.instructions.find(tran => tran.cashieringRqstId === this.standingAuthId) ?? this.defaultInstruction
        this.currentTransaction = this.convertTableDataToTransactionType(this.instructionToDelete);

        if (this.currentTransaction.mediaTypeCd.match(MediaTypeCode.A)) {
          this.isAch = true;
        }

        this.title = StandingAuthDeleteMessaging.deleteTitle;
        this.message= StandingAuthDeleteMessaging.deleteMessage;
        this.continueButtonTxt = StandingAuthDeleteMessaging.deleteButtonText;
    });

    this.store.select(selectSubmitDeleteStandingAuthState).pipe(takeUntil(this.destroy$)).subscribe(submitDeleteTransaction => {
      if (submitDeleteTransaction.isCancelTransactionCallFinished && !submitDeleteTransaction.isNewAchProfile) {
        if (submitDeleteTransaction.isCancelTransactionSubmitted && !submitDeleteTransaction.hasCancelTransactionError) {
          this.reloadCurrentPage();
          this.modal.hideModal();
          this.toastr.success("Standing authorization deleted");
          this.store.dispatch(resetSubmitCancelTransactionState());
        } else if (submitDeleteTransaction.hasPendingTransactions)
        {
          this.pendingerror = true;
          this.title =  "This standing authorization cannot be deactivated at this time";
          this.message = "There is at least one pending transaction using this standing authorization. To deactivate this standing authorization, you must either cancel pending transactions, or return after the transactions have been completed.";
          this.continueButtonTxt = "OK";
          this.store.dispatch(resetSubmitCancelTransactionState());
        } else if (submitDeleteTransaction.hasCancelTransactionError){
          this.modal.hideModal();
          this.toastr.error("Failed to delete standing authorization");
          this.store.dispatch(resetSubmitCancelTransactionState());
        }
      }
    })
  }


  ngAfterViewInit(): void {
    this.modal = document.getElementById(this.modalId) as EverestModal;
    this.showModalSub = this.showModal$.subscribe((show) => {
      if (show) {this.modal.showModal()};
    })
  }

  ngOnDestroy(): void {
    this.showModalSub?.unsubscribe();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onCancelClick(): void{
    this.modal.hideModal();
    if (this.pendingerror){
      this.store.dispatch(resetSubmitCancelTransactionState());
      this.pendingerror = false;
    }
  }

  onViewClick(): void{
    this.modal.hideModal();
  }
  onConfirmClick(): void{
    this.store.dispatch(resetSubmitCancelTransactionState());
    this.store.dispatch(deleteTransactionClicked({ transaction: this.currentTransaction }));    
  }

  reloadCurrentPage(): void {
    // always refresh transaction table after successful cancellation, including on view page as transaction table
    // does not refresh automatically when returning to transaction table from view page
    this.store.dispatch(resetSubmitCancelTransactionState());
    this.store.dispatch(StandingAuthorizationsTableActions.refresh({ accountId: this.subAccountId }));
    var el = document.getElementById("view-page");
    if (el !== null) {
      this.refreshViewTransactionPage();
    }
  }

  convertTableDataToTransactionType(tableEntry: StandingInstruction): CashieringTransaction | RecurringTransaction |Sloa
  {
    if (this.isAch)
    {
      this.transactionType = this.mapTransactionType(tableEntry.transactionType, tableEntry);
      this.toFrom = tableEntry.to1 + tableEntry.to2 + tableEntry.to3
      this.status = tableEntry.statusCodeDescription? tableEntry.statusCodeDescription : "";
      return {
        processDate: tableEntry.processDate,
        outgoingAmount: tableEntry.outgoingAmount,
        incomingAmount: tableEntry.incomingAmount,
        txnCode: tableEntry.txnCode,
        frequencyCd: tableEntry.frequencyCode,
        frequencyDescription: tableEntry.frequencyDescription,
        to1: tableEntry.to1,
        to2: tableEntry.to2,
        to3: tableEntry.to3,
        isViewable: tableEntry.isViewable,
        isEditable: tableEntry.isEditable,
        isCancelable: tableEntry.isCancelable,
        isReactivatable: tableEntry.isReactivatable,
        cashieringRqstId: tableEntry.cashieringRqstId,
        cashieringRqstSecondId: tableEntry.cashieringRqstSecondId,
        mediaTypeCd: tableEntry.mediaTypeCd,
        typeDescription: tableEntry.typeDescription,
        statusCd: tableEntry.statusCd,
        statusCodeDescription: tableEntry.statusCodeDescription,
        bundledSloaIn: tableEntry.bundledSloaIn,
        thirdPartyInd: tableEntry.thirdPartyInd,
        faMasterAcct: tableEntry.faMasterAcct,
        address: tableEntry.address
      } as RecurringTransaction;
    }
    else if (tableEntry.isSloaSetup || tableEntry.isReactivatable)
    {
      this.transactionType = this.mapTransactionType(tableEntry.transactionType, tableEntry);
      this.nickname = tableEntry.nickname? tableEntry.nickname : "";
      this.toFrom = tableEntry.to1 + tableEntry.to2 + tableEntry.to3
      this.status = tableEntry.statusCodeDescription? tableEntry.statusCodeDescription : "";
      return tableEntry as CashieringTransaction;
    }
    else
    {
      this.transactionType = this.mapTransactionType(tableEntry.transactionType, tableEntry);
      this.nickname = tableEntry.nickname? tableEntry.nickname : "";
      this.toFrom = tableEntry.to1 + tableEntry.to2 + tableEntry.to3
      this.status = tableEntry.statusCodeDescription? tableEntry.statusCodeDescription : "";
      return tableEntry as Sloa;
    }
  }

  mapTransactionType(type: TransactionType, instruction: StandingInstruction)
  {
    var descriptionSuffix = instruction.descriptionSuffix ?? '';
      if (descriptionSuffix.includes("Set-Up -")){
        let splitSuffix = descriptionSuffix.split('-');
        descriptionSuffix = splitSuffix[2];
      }
      if (instruction.frequencyDescription != null && instruction.frequencyDescription != undefined) {
        descriptionSuffix += " - " + instruction.frequencyDescription;
      }
      let suffix = '';
      if (descriptionSuffix !== '') {
        suffix = ` - ${descriptionSuffix}`;
      }

      if (type === transactionTypes.internalTransfer) {
        return `Internal transfer${suffix}`;
      } else if (
        type === transactionTypes.ach &&
        (instruction.fundsDirection === transactionDirection.in || (instruction.incomingAmount != undefined && instruction.incomingAmount != null))
      ) {
        return `ACH in${suffix}`;
      } else if (
        type === transactionTypes.ach &&
        (instruction.fundsDirection === transactionDirection.out || (instruction.outgoingAmount != undefined && instruction.outgoingAmount != null))
      ) {
        return `ACH out${suffix}`;
      } else if (type === transactionTypes.check) {
        return `Check${suffix}`;
      } else if (type === transactionTypes.wire) {
        return `Wire${suffix}`;
      } else return type;
  }

  refreshViewTransactionPage(): void {
    //temporary fix while we continue to get these fields from the overview call
    this.instructionToDelete = {
      ...this.instructionToDelete,
      isCancelable: false,
      isEditable: false
    }
    if (this.instructionToDelete.mediaTypeCd.match(MediaTypeCode.C)) {
      this.store.dispatch(CheckActions.callInquiry({
        payload: this.instructionToDelete,
        viewType: 'standing-auth',
        transactionType: transactionTypes.check
      }))
    } else if (this.instructionToDelete.mediaTypeCd.includes(MediaTypeCode.W)) {
      this.store.dispatch(WireActions.callInquiry({
        payload: this.instructionToDelete,
        viewType: 'standing-auth',
        transactionType: transactionTypes.wireSloa,
        resourceVersion: '3'
      }));
    } else if (this.instructionToDelete.mediaTypeCd.match(MediaTypeCode.J)) {
      this.store.dispatch(InternalTransferActions.callInquiry({
        payload: this.instructionToDelete,
        viewType: 'standing-auth',
        transactionType: transactionTypes.internalTransfer
      }));
    } else if (this.instructionToDelete.mediaTypeCd.includes(MediaTypeCode.A)) {
      this.store.dispatch(AchActions.callInquiry({
        payload: this.instructionToDelete,
        viewType: 'standing-auth',
        transactionType: transactionTypes.ach
      }));
    }
  }
}


type EverestModal = HTMLElement & {
  showModal: () => {},
  hideModal: () => {}
};
